import type { SmallUpsConfigurationsSaveHandle } from './subsections/smallUpsConfigurationsSaveHandle';
import type { SelectedFile } from '../../../components/forms/FileUpload';
import type { RepeatableInstanceSaveHandle } from '../../../components/repeatableSections/repeatableInstanceSaveHandle';
import type { SavedAttachment } from '../../../services/attachmentService';
import type { Request } from '../../../types';
import type { SmallUpsConfiguration } from '../../../types/smallUps';

export interface SmallUpsInitialProps {
  formData: Record<string, unknown> & {
    stage?: string;
    sysRequestStage?: string;
  };
  updateFormData: (
    field: string,
    value: string | number | boolean | SmallUpsConfiguration[] | undefined,
  ) => void;
  requestId?: string;
  disabled?: boolean;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  isEditing?: boolean;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  registerSmallUpsSaveHandle?: (
    handle: SmallUpsConfigurationsSaveHandle | null,
  ) => void;
  onSmallUpsEditorActiveChange?: (active: boolean) => void;
}

export interface SmallUpsPersistedProps {
  request: Request;
  isEditing: boolean;
  editedData: Record<string, unknown>;
  updateEditedData: (field: string, value: string | boolean) => void;
  showFullDetails?: boolean;
  showViewToggle?: boolean;
  isAdvancedView?: boolean;
  onToggleView?: () => void;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  /**
   * Request Detail engine wiring (#997 Story 6.4 Phase 4b). The engine section
   * registers its save handle with the screen's `useSmallUpsEngineSaveBridge`
   * and writes the live session list into `editedData.smallUpsConfigurations`
   * via `applyEditedDataPatch` so it survives step navigation.
   */
  registerInstanceSaveHandle?: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  /** True when `editedData.smallUpsConfigurations` was touched this edit session. */
  smallUpsConfigurationsDirty?: boolean;
  onSmallUpsEditorActiveChange?: (active: boolean) => void;
}

export type SmallUpsProps = SmallUpsInitialProps | SmallUpsPersistedProps;

export function isSmallUpsPersistedProps(
  p: SmallUpsProps,
): p is SmallUpsPersistedProps {
  return 'request' in p && (p as SmallUpsPersistedProps).request != null;
}
